import React, { useState } from 'react';
import styles from './FilterGroup.module.scss';

/**
 * Collapsible filter group component
 * Shows a group of checkbox filters that can be expanded/collapsed
 *
 * @param {Object} props
 * @param {string} props.title - Group title
 * @param {string} props.handle - Filter handle (key)
 * @param {Array} props.options - Array of {value, label} options
 * @param {Array} props.selected - Array of selected values
 * @param {Array} props.availableValues - Array of values that have matching products (optional)
 * @param {Function} props.onChange - Callback when selection changes
 */
export default function FilterGroup({ title, handle, options, selected = [], availableValues = null, onChange }) {
    const [isOpen, setIsOpen] = useState(false);

    const handleCheckboxChange = (optionValue) => {
        const newSelected = selected.includes(optionValue)
            ? selected.filter(v => v !== optionValue)
            : [...selected, optionValue];

        onChange(handle, newSelected);
    };

    const hasSelected = selected.length > 0;

    // Check if any options in this group are available
    const hasAvailableOptions = availableValues === null ||
        options.some(option =>
            availableValues.includes(option.value) || selected.includes(option.value)
        );

    return (
        <li className={`${styles.mainCategoryItem} ${(isOpen || hasSelected) && hasAvailableOptions ? styles.active : ''} ${!hasAvailableOptions ? styles.noAvailableFilters : ''}`}>
            <div
                className={styles.mainCategory}
                onClick={() => setIsOpen(!isOpen)}
            >
                {title}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                >
                    <path fill="currentColor" d="M7 10l5 5 5-5z" />
                </svg>
            </div>
            {(isOpen || hasSelected) && (
                <ul className={styles.subCategoryItem}>
                    {options.map((option) => {
                        // Determine if option should be disabled
                        // If availableValues is null, all options are enabled (default behavior)
                        // If it's an array, only enable options that are in the array OR already selected
                        const isAvailable = availableValues === null ||
                                          availableValues.includes(option.value) ||
                                          selected.includes(option.value);

                        return (
                            <li
                                key={option.value}
                                className={!isAvailable ? styles.disabled : ''}
                            >
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(option.value)}
                                        disabled={!isAvailable}
                                        onChange={() => handleCheckboxChange(option.value)}
                                    />
                                    {option.label}
                                </label>
                            </li>
                        );
                    })}
                </ul>
            )}
        </li>
    );
}
